import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../../../utils/api';
import { reportUtils } from '../utils/reportUtils';

const AIReportGeneratorModal = ({ isOpen, onClose, onGenerate, sites = [] }) => {
  const [formData, setFormData] = useState({
    siteId: '',
    siteName: '',
    reportTitle: '',
    startDate: '',
    endDate: '',
    // Additional data points
    programmeStatus: 'on-time',
    qualityInspections: 'yes',
    safetyIncidents: 'no',
    budgetStatus: 'on-budget',
    // Photo settings
    progressPhotosToShow: '',
    beforeAfterPhotosToShow: '',
    // Import settings
    importTechnicalDrawings: false
  });
  
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showConfirmation, setShowConfirmation] = useState(false);
  
  // Photo counts from database
  const [photoCounts, setPhotoCounts] = useState({
    progressPhotos: 0,
    beforeAfterPhotos: 0,
    isLoading: false
  });
  
  // Credit cost for report generation
  const [creditCost, setCreditCost] = useState({
    cost: 10, // Default cost
    isLoading: false
  });

  // Fetch credit costs from API
  const fetchCreditCosts = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/neo/credit-costs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success && response.data.credit_costs) {
        setCreditCost({
          cost: response.data.credit_costs.report_generation || 10,
          isLoading: false
        });
      }
    } catch (err) {
      console.error('Failed to fetch credit costs:', err);
      // Keep default cost on error
      setCreditCost({ cost: 10, isLoading: false });
    }
  }, []);

  // Fetch photo counts from API
  const fetchPhotoCounts = useCallback(async (startDate, endDate, siteId) => {
    if (!startDate || !endDate) return;
    
    setPhotoCounts(prev => ({ ...prev, isLoading: true }));
    
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        start_date: startDate,
        end_date: endDate
      });
      if (siteId) {
        params.append('site_id', siteId);
      }
      
      const response = await axios.get(`${API}/admin/work-diary-photos/count?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setPhotoCounts({
        progressPhotos: response.data.total_count || 0,
        beforeAfterPhotos: response.data.total_count || 0,
        isLoading: false
      });
    } catch (err) {
      console.error('Failed to fetch photo counts:', err);
      setPhotoCounts({
        progressPhotos: 0,
        beforeAfterPhotos: 0,
        isLoading: false
      });
    }
  }, []);

  // Reset form when modal opens - use same default dates as cover page
  useEffect(() => {
    if (isOpen) {
      // Use the same date defaults as the cover page
      const startDate = reportUtils.getFirstWorkingDayOfMonth();
      const endDate = reportUtils.getTodayDate();
      
      // Generate default report title with current month/year
      const now = new Date();
      const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                          'July', 'August', 'September', 'October', 'November', 'December'];
      const defaultTitle = `Progress Report - ${monthNames[now.getMonth()]} ${now.getFullYear()}`;
      
      setFormData({
        siteId: '',
        siteName: '',
        reportTitle: defaultTitle,
        startDate: startDate,
        endDate: endDate,
        // Reset additional data points to defaults
        programmeStatus: 'on-time',
        qualityInspections: 'yes',
        safetyIncidents: 'no',
        budgetStatus: 'on-budget',
        // Reset photo settings
        progressPhotosToShow: '',
        beforeAfterPhotosToShow: '',
        // Reset import settings
        importTechnicalDrawings: false
      });
      setError('');
      setShowConfirmation(false);
      
      // Fetch initial photo counts and credit costs
      fetchPhotoCounts(startDate, endDate, '');
      fetchCreditCosts();
    }
  }, [isOpen, fetchPhotoCounts, fetchCreditCosts]);

  // Refetch photo counts when dates or site changes
  useEffect(() => {
    if (isOpen && formData.startDate && formData.endDate) {
      fetchPhotoCounts(formData.startDate, formData.endDate, formData.siteId);
    }
  }, [isOpen, formData.startDate, formData.endDate, formData.siteId, fetchPhotoCounts]);

  // Update site name when site is selected
  const handleSiteChange = (e) => {
    const selectedSiteId = e.target.value;
    const selectedSite = sites.find(s => s.id === selectedSiteId);
    
    // Generate title with site name
    const now = new Date();
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                        'July', 'August', 'September', 'October', 'November', 'December'];
    const siteName = selectedSite?.site_name || '';
    const newTitle = siteName 
      ? `${siteName} - Progress Report - ${monthNames[now.getMonth()]} ${now.getFullYear()}`
      : `Progress Report - ${monthNames[now.getMonth()]} ${now.getFullYear()}`;
    
    setFormData(prev => ({
      ...prev,
      siteId: selectedSiteId,
      siteName: siteName,
      reportTitle: newTitle
    }));
  };

  // Format date for display
  const formatDateRange = () => {
    if (!formData.startDate || !formData.endDate) return '';
    
    const start = new Date(formData.startDate);
    const end = new Date(formData.endDate);
    
    const options = { day: '2-digit', month: 'short', year: 'numeric' };
    return `${start.toLocaleDateString('en-GB', options)} - ${end.toLocaleDateString('en-GB', options)}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.siteId) {
      setError('Please select a site');
      return;
    }
    if (!formData.reportTitle.trim()) {
      setError('Please enter a report title');
      return;
    }
    if (!formData.startDate || !formData.endDate) {
      setError('Please select a reporting period');
      return;
    }
    
    // Validate date range
    const start = new Date(formData.startDate);
    const end = new Date(formData.endDate);
    if (start > end) {
      setError('Start date must be before end date');
      return;
    }
    
    setError('');
    // Show confirmation dialog instead of immediately generating
    setShowConfirmation(true);
  };

  const handleConfirmGenerate = async () => {
    setIsLoading(true);
    
    try {
      // Calculate actual photo counts to use
      const progressPhotos = formData.progressPhotosToShow.trim() === '' 
        ? photoCounts.progressPhotos 
        : parseInt(formData.progressPhotosToShow) || photoCounts.progressPhotos;
      const beforeAfterPhotos = formData.beforeAfterPhotosToShow.trim() === '' 
        ? photoCounts.beforeAfterPhotos 
        : parseInt(formData.beforeAfterPhotosToShow) || photoCounts.beforeAfterPhotos;
      
      // Pass the form data to parent for processing
      await onGenerate({
        siteId: formData.siteId,
        siteName: formData.siteName,
        reportTitle: formData.reportTitle,
        startDate: formData.startDate,
        endDate: formData.endDate,
        // Additional data points
        programmeStatus: formData.programmeStatus,
        qualityInspections: formData.qualityInspections,
        safetyIncidents: formData.safetyIncidents,
        budgetStatus: formData.budgetStatus,
        // Photo settings
        progressPhotosToShow: progressPhotos,
        beforeAfterPhotosToShow: beforeAfterPhotos,
        totalProgressPhotosAvailable: photoCounts.progressPhotos,
        totalBeforeAfterPhotosAvailable: photoCounts.beforeAfterPhotos,
        // Import settings
        importTechnicalDrawings: formData.importTechnicalDrawings,
        // Credit cost
        creditCost: creditCost.cost
      });
      
      setShowConfirmation(false);
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to initialize report generation');
      setShowConfirmation(false);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  // Confirmation Dialog
  if (showConfirmation) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        {/* Backdrop */}
        <div 
          className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          onClick={() => setShowConfirmation(false)}
        />
        
        {/* Confirmation Modal */}
        <div className="relative bg-slate-800 rounded-xl shadow-2xl border border-slate-700 w-full max-w-md mx-4 overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-purple-600 to-indigo-600 px-6 py-4">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-white/20 rounded-lg">
                <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4zm2 6a2 2 0 012-2h8a2 2 0 012 2v4a2 2 0 01-2 2H8a2 2 0 01-2-2v-4zm6 4a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                </svg>
              </div>
              <div>
                <h2 className="text-lg font-bold text-white">Confirm Report Generation</h2>
                <p className="text-purple-200 text-sm">Review cost before proceeding</p>
              </div>
            </div>
          </div>

          {/* Body */}
          <div className="p-6 space-y-4">
            {/* Report Summary */}
            <div className="bg-slate-700/50 rounded-lg p-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Report:</span>
                <span className="text-white font-medium truncate ml-2 max-w-[200px]">{formData.reportTitle}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Site:</span>
                <span className="text-white">{formData.siteName}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Period:</span>
                <span className="text-white">{formatDateRange()}</span>
              </div>
            </div>

            {/* Credit Cost */}
            <div className="bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-500/30 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-amber-500/20 rounded-lg">
                    <svg className="w-5 h-5 text-amber-400" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M11 3a1 1 0 10-2 0v1a1 1 0 102 0V3zM15.657 5.757a1 1 0 00-1.414-1.414l-.707.707a1 1 0 001.414 1.414l.707-.707zM18 10a1 1 0 01-1 1h-1a1 1 0 110-2h1a1 1 0 011 1zM5.05 6.464A1 1 0 106.464 5.05l-.707-.707a1 1 0 00-1.414 1.414l.707.707zM5 10a1 1 0 01-1 1H3a1 1 0 110-2h1a1 1 0 011 1zM8 16v-1h4v1a2 2 0 11-4 0zM12 14c.015-.34.208-.646.477-.859a4 4 0 10-4.954 0c.27.213.462.519.476.859h4.002z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-amber-300">Neo Credits Cost</p>
                    <p className="text-xs text-amber-200/70">Deducted from your balance</p>
                  </div>
                </div>
                <div className="text-right">
                  <span className="text-2xl font-bold text-amber-400">{creditCost.cost}</span>
                  <span className="text-sm text-amber-300 ml-1">credits</span>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end space-x-3 pt-2">
              <button
                type="button"
                onClick={() => setShowConfirmation(false)}
                className="px-4 py-2 text-sm font-medium text-gray-300 hover:text-white bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors"
              >
                Back
              </button>
              <button
                onClick={handleConfirmGenerate}
                disabled={isLoading}
                className="px-6 py-2 text-sm font-medium text-white bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                data-testid="ai-report-confirm-generate-btn"
              >
                {isLoading ? (
                  <>
                    <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    <span>Generating...</span>
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    <span>Confirm & Generate</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop - click disabled */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
      />
      
      {/* Modal */}
      <div className="relative bg-slate-800 rounded-xl shadow-2xl border border-slate-700 w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-white/20 rounded-lg">
                <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M11 3a1 1 0 10-2 0v1a1 1 0 102 0V3zM15.657 5.757a1 1 0 00-1.414-1.414l-.707.707a1 1 0 001.414 1.414l.707-.707zM18 10a1 1 0 01-1 1h-1a1 1 0 110-2h1a1 1 0 011 1zM5.05 6.464A1 1 0 106.464 5.05l-.707-.707a1 1 0 00-1.414 1.414l.707.707zM5 10a1 1 0 01-1 1H3a1 1 0 110-2h1a1 1 0 011 1zM8 16v-1h4v1a2 2 0 11-4 0zM12 14c.015-.34.208-.646.477-.859a4 4 0 10-4.954 0c.27.213.462.519.476.859h4.002z" />
                </svg>
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">AI Report Generator</h2>
                <p className="text-purple-200 text-sm">Auto-generate report content from your data</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-1 hover:bg-white/20 rounded-lg transition-colors"
            >
              <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5 overflow-y-auto flex-1">
          {/* Error Message */}
          {error && (
            <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-3 flex items-start space-x-2">
              <svg className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <span className="text-red-300 text-sm">{error}</span>
            </div>
          )}

          {/* Site Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Site <span className="text-red-400">*</span>
            </label>
            <select
              value={formData.siteId}
              onChange={handleSiteChange}
              className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2.5 text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
              data-testid="ai-report-site-select"
            >
              <option value="">Select a site...</option>
              {sites.map(site => (
                <option key={site.id} value={site.id}>
                  {site.site_name}
                </option>
              ))}
            </select>
          </div>

          {/* Report Title */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Report Title <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              value={formData.reportTitle}
              onChange={(e) => setFormData(prev => ({ ...prev, reportTitle: e.target.value }))}
              placeholder="Enter report title"
              className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2.5 text-white placeholder-gray-500 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
              data-testid="ai-report-title-input"
            />
          </div>

          {/* Reporting Period - Matching Cover Page Style */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Reporting Period <span className="text-red-400">*</span>
            </label>
            
            {/* Date Range Inputs - Same as Cover Page */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-gray-400 mb-1">Start Date</label>
                <input
                  type="date"
                  value={formData.startDate}
                  onChange={(e) => setFormData(prev => ({ ...prev, startDate: e.target.value }))}
                  className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all text-sm"
                  data-testid="ai-report-start-date"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">End Date</label>
                <input
                  type="date"
                  value={formData.endDate}
                  onChange={(e) => setFormData(prev => ({ ...prev, endDate: e.target.value }))}
                  className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all text-sm"
                  data-testid="ai-report-end-date"
                />
              </div>
            </div>
            
            {/* Selected Period Display */}
            {formatDateRange() && (
              <div className="mt-2 text-sm text-purple-400">
                Period: {formatDateRange()}
              </div>
            )}
          </div>

          {/* Photo Selection Settings */}
          <div className="space-y-3">
            <label className="block text-sm font-medium text-gray-300">
              Photos to Include
            </label>
            
            {/* Progress Photos */}
            <div className="flex items-center space-x-3">
              <label className="text-xs text-gray-400 w-44">Progress Photos to show:</label>
              <div className="flex-1 flex items-center space-x-2">
                <input
                  type="number"
                  min="0"
                  max={photoCounts.progressPhotos}
                  value={formData.progressPhotosToShow}
                  onChange={(e) => setFormData(prev => ({ ...prev, progressPhotosToShow: e.target.value }))}
                  placeholder="All"
                  className="w-20 bg-slate-700 border border-slate-600 rounded-lg px-3 py-1.5 text-white text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all placeholder-gray-500"
                  data-testid="ai-report-progress-photos"
                />
                <span className="text-xs text-gray-500">|</span>
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, progressPhotosToShow: '' }))}
                  className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-all ${
                    formData.progressPhotosToShow === ''
                      ? 'bg-purple-500/20 border-purple-500 text-purple-300'
                      : 'bg-slate-700 border-slate-600 text-gray-400 hover:border-slate-500'
                  }`}
                >
                  All ({photoCounts.isLoading ? '...' : photoCounts.progressPhotos})
                </button>
              </div>
            </div>

            {/* Before/After Photos */}
            <div className="flex items-center space-x-3">
              <label className="text-xs text-gray-400 w-44">Before/After Photos to show:</label>
              <div className="flex-1 flex items-center space-x-2">
                <input
                  type="number"
                  min="0"
                  max={photoCounts.beforeAfterPhotos}
                  value={formData.beforeAfterPhotosToShow}
                  onChange={(e) => setFormData(prev => ({ ...prev, beforeAfterPhotosToShow: e.target.value }))}
                  placeholder="All"
                  className="w-20 bg-slate-700 border border-slate-600 rounded-lg px-3 py-1.5 text-white text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all placeholder-gray-500"
                  data-testid="ai-report-before-after-photos"
                />
                <span className="text-xs text-gray-500">|</span>
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, beforeAfterPhotosToShow: '' }))}
                  className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-all ${
                    formData.beforeAfterPhotosToShow === ''
                      ? 'bg-purple-500/20 border-purple-500 text-purple-300'
                      : 'bg-slate-700 border-slate-600 text-gray-400 hover:border-slate-500'
                  }`}
                >
                  All ({photoCounts.isLoading ? '...' : photoCounts.beforeAfterPhotos})
                </button>
              </div>
            </div>
          </div>

          {/* Additional Data Points */}
          <div className="space-y-4">
            <label className="block text-sm font-medium text-gray-300">
              Project Status Indicators
            </label>
            
            {/* Programme Status */}
            <div>
              <label className="block text-xs text-gray-400 mb-2">Programme</label>
              <div className="flex space-x-2">
                {[
                  { value: 'delayed', label: 'Delayed', color: 'red' },
                  { value: 'on-time', label: 'On-time', color: 'green' },
                  { value: 'ahead', label: 'Ahead', color: 'blue' }
                ].map(option => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, programmeStatus: option.value }))}
                    className={`flex-1 px-3 py-2 text-xs font-medium rounded-lg border transition-all ${
                      formData.programmeStatus === option.value
                        ? option.color === 'red' 
                          ? 'bg-red-500/20 border-red-500 text-red-300'
                          : option.color === 'green'
                            ? 'bg-green-500/20 border-green-500 text-green-300'
                            : 'bg-blue-500/20 border-blue-500 text-blue-300'
                        : 'bg-slate-700 border-slate-600 text-gray-400 hover:border-slate-500'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Quality & Safety Row */}
            <div className="grid grid-cols-2 gap-4">
              {/* Quality Inspections */}
              <div>
                <label className="block text-xs text-gray-400 mb-2">Quality Inspections Carried Out</label>
                <div className="flex space-x-2">
                  {[
                    { value: 'yes', label: 'Yes' },
                    { value: 'no', label: 'No' }
                  ].map(option => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, qualityInspections: option.value }))}
                      className={`flex-1 px-3 py-2 text-xs font-medium rounded-lg border transition-all ${
                        formData.qualityInspections === option.value
                          ? option.value === 'yes'
                            ? 'bg-green-500/20 border-green-500 text-green-300'
                            : 'bg-amber-500/20 border-amber-500 text-amber-300'
                          : 'bg-slate-700 border-slate-600 text-gray-400 hover:border-slate-500'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Safety Incidents */}
              <div>
                <label className="block text-xs text-gray-400 mb-2">Safety Incidents</label>
                <div className="flex space-x-2">
                  {[
                    { value: 'yes', label: 'Yes' },
                    { value: 'no', label: 'No' }
                  ].map(option => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, safetyIncidents: option.value }))}
                      className={`flex-1 px-3 py-2 text-xs font-medium rounded-lg border transition-all ${
                        formData.safetyIncidents === option.value
                          ? option.value === 'yes'
                            ? 'bg-red-500/20 border-red-500 text-red-300'
                            : 'bg-green-500/20 border-green-500 text-green-300'
                          : 'bg-slate-700 border-slate-600 text-gray-400 hover:border-slate-500'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Budget Status */}
            <div>
              <label className="block text-xs text-gray-400 mb-2">Budget</label>
              <div className="flex space-x-2">
                {[
                  { value: 'under-budget', label: 'Under Budget', color: 'green' },
                  { value: 'on-budget', label: 'On Budget', color: 'blue' },
                  { value: 'over-budget', label: 'Over Budget', color: 'red' }
                ].map(option => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, budgetStatus: option.value }))}
                    className={`flex-1 px-3 py-2 text-xs font-medium rounded-lg border transition-all ${
                      formData.budgetStatus === option.value
                        ? option.color === 'green'
                          ? 'bg-green-500/20 border-green-500 text-green-300'
                          : option.color === 'blue'
                            ? 'bg-blue-500/20 border-blue-500 text-blue-300'
                            : 'bg-red-500/20 border-red-500 text-red-300'
                        : 'bg-slate-700 border-slate-600 text-gray-400 hover:border-slate-500'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Import from Previous Report */}
          <div className="space-y-3">
            <label className="block text-sm font-medium text-gray-300">
              Import from Previous Report
            </label>
            
            <label className="flex items-start space-x-3 cursor-pointer group">
              <div className="relative flex-shrink-0 mt-0.5">
                <input
                  type="checkbox"
                  checked={formData.importTechnicalDrawings}
                  onChange={(e) => setFormData(prev => ({ ...prev, importTechnicalDrawings: e.target.checked }))}
                  className="sr-only"
                  data-testid="ai-report-import-drawings-checkbox"
                />
                <div className={`w-5 h-5 border-2 rounded transition-all flex items-center justify-center ${
                  formData.importTechnicalDrawings 
                    ? 'bg-purple-600 border-purple-600' 
                    : 'border-slate-500 bg-slate-700 group-hover:border-slate-400'
                }`}>
                  {formData.importTechnicalDrawings && (
                    <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  )}
                </div>
              </div>
              <div>
                <span className="text-sm text-gray-300 group-hover:text-white transition-colors">
                  Import technical drawings from previous report
                </span>
                <p className="text-xs text-gray-500">
                  Section 13.0 will be populated from the last saved report for this site
                </p>
              </div>
            </label>
          </div>

          {/* Data Note */}
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
            <div className="flex items-start space-x-2">
              <svg className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <p className="text-xs text-amber-200/80">
                <span className="font-medium text-amber-300">Note:</span> The generated report is based on the data held for your organisation. The more information available (tasks, photos, attendance records, expenses), the more comprehensive and accurate the report will be.
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-300 hover:text-white bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-6 py-2 text-sm font-medium text-white bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              data-testid="ai-report-generate-btn"
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  <span>Initializing...</span>
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M11 3a1 1 0 10-2 0v1a1 1 0 102 0V3zM15.657 5.757a1 1 0 00-1.414-1.414l-.707.707a1 1 0 001.414 1.414l.707-.707zM18 10a1 1 0 01-1 1h-1a1 1 0 110-2h1a1 1 0 011 1zM5.05 6.464A1 1 0 106.464 5.05l-.707-.707a1 1 0 00-1.414 1.414l.707.707zM5 10a1 1 0 01-1 1H3a1 1 0 110-2h1a1 1 0 011 1zM8 16v-1h4v1a2 2 0 11-4 0zM12 14c.015-.34.208-.646.477-.859a4 4 0 10-4.954 0c.27.213.462.519.476.859h4.002z" />
                  </svg>
                  <span>Generate Report</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AIReportGeneratorModal;
